import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext, type AuthContextValue } from './AuthContext';
import { AuthApi } from '@/features/auth/api';
import { useCustomerInfo } from '@/features/auth/hooks/useCustomerInfo';
import { authKeys } from '@/features/auth/queryKeys';
import { AuthToken } from '@/shared/api/authToken';
import { STORAGE_KEYS } from '@/lib/constants/storage';
import { clearAccountBillingPlanType } from '@/features/account/useAccountPermissions';
import { PermissionUtils } from './PermissionUtils';
import type { IAuthenticatedCPUser } from '@/lib/types/Auth';

function getStoredAuth(): IAuthenticatedCPUser | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEYS.AUTH_DATA);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function getStoredSelectedCId(): number | null {
  const stored = sessionStorage.getItem(STORAGE_KEYS.SELECTED_CID);
  return stored ? parseInt(stored, 10) : null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  // Core auth state - synchronous from session storage
  const [authData, setAuthData] = useState<IAuthenticatedCPUser | null>(getStoredAuth);
  const [isLoading, setIsLoading] = useState(false);

  // Selected customer ID - from localStorage with fallback to user's cId
  const [selectedCId, setSelectedCIdState] = useState<number | null>(() => {
    const stored = getStoredSelectedCId();
    const userCId = getStoredAuth()?.cpuser?.core?.cId;
    return stored ?? userCId ?? null;
  });

  const isAuthenticated = authData !== null;
  const userCId = authData?.cpuser?.core?.cId ?? null;

  // Fetch customer info when authenticated (using TanStack Query)
  const { data: customerInfo, isLoading: isCustomerLoading } = useCustomerInfo(
    userCId,
    { enabled: isAuthenticated && userCId !== null }
  );

  // Configure 401 handler on mount
  useEffect(() => {
    AuthToken.setHandle401(() => {
      // Clear state — including sessionStorage AUTH_DATA, or the post-redirect
      // reload would rehydrate the now-invalid session and loop straight back in.
      setAuthData(null);
      setSelectedCIdState(null);
      sessionStorage.removeItem(STORAGE_KEYS.AUTH_DATA);
      sessionStorage.removeItem(STORAGE_KEYS.SELECTED_CID);
      clearAccountBillingPlanType();
      queryClient.clear();
      // Preserve where the user was so they return there after re-login, and
      // flag the expiry so the login page can explain the forced sign-out.
      sessionStorage.setItem(STORAGE_KEYS.SESSION_EXPIRED, '1');
      const returnTo = window.location.pathname + window.location.search;
      const redirect =
        returnTo && !returnTo.startsWith('/login')
          ? `?redirect=${encodeURIComponent(returnTo)}`
          : '';
      window.location.href = `/login${redirect}`;
    });
  }, [queryClient]);

  // Validate and update selectedCId when customerInfo loads
  useEffect(() => {
    if (!customerInfo || userCId === null) return;

    const validCIds = [
      customerInfo.customer.cfg.cId,
      ...customerInfo.subcustomers.map(s => s.cfg.cId)
    ];
    const fallbackCId = validCIds.includes(userCId)
      ? userCId
      : customerInfo.customer.cfg.cId;

    // If stored selectedCId is invalid, reset to user's cId
    if (selectedCId && !validCIds.includes(selectedCId)) {
      setSelectedCIdState(fallbackCId);
      sessionStorage.setItem(STORAGE_KEYS.SELECTED_CID, String(fallbackCId));
    }

    // If no selectedCId set, default to user's cId
    if (!selectedCId) {
      setSelectedCIdState(fallbackCId);
      sessionStorage.setItem(STORAGE_KEYS.SELECTED_CID, String(fallbackCId));
    }
  }, [customerInfo, selectedCId, userCId]);

  // Derived state
  const selectedCustomer = useMemo(() => {
    if (!customerInfo || !selectedCId) return null;
    if (customerInfo.customer.cfg.cId === selectedCId) {
      return customerInfo.customer;
    }
    return customerInfo.subcustomers.find(s => s.cfg.cId === selectedCId) ?? null;
  }, [customerInfo, selectedCId]);

  const subcustomers = customerInfo?.subcustomers ?? [];

  const isSubaccount = selectedCustomer != null && !!selectedCustomer.cfg.parentCId;
  const isParentAccount = !isSubaccount;

  const canAccessSubAccounts = useMemo(() => {
    if (!authData || !customerInfo) return false;
    const perms = authData.cpuser.permissions;
    return (
      perms.subcustomerRead &&
      perms.subcustomerWrite &&
      customerInfo.subcustomers.length > 0
    );
  }, [authData, customerInfo]);

  // Synchronous permission derivations — available the moment login commits,
  // so router guards and post-login redirects need no loading state.
  const isUploadOnly = useMemo(() => PermissionUtils.isUploadOnly(authData), [authData]);
  const canViewMedia = useMemo(
    () => (authData ? PermissionUtils.hasMediaAccess(authData.cpuser.permissions) : false),
    [authData]
  );

  // Actions
  const login = useCallback(async (username: string, password: string) => {
    setIsLoading(true);
    try {
      const result = await AuthApi.login(username, password);

      // Store auth data
      sessionStorage.setItem(STORAGE_KEYS.AUTH_DATA, JSON.stringify(result));
      setAuthData(result);

      // Set initial selectedCId to user's cId
      const userCId = result.cpuser.core.cId;

      // Always initialize selected customer from the authenticated user to avoid
      // cross-account localStorage bleed (e.g. previous user's selected cId).
      setSelectedCIdState(userCId);
      sessionStorage.setItem(STORAGE_KEYS.SELECTED_CID, String(userCId));

      // Invalidate any stale queries
      await queryClient.invalidateQueries({ queryKey: authKeys.all });
    } finally {
      setIsLoading(false);
    }
  }, [queryClient]);

  const logout = useCallback(() => {
    // Clear session storage
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.SELECTED_CID);

    // Reset state
    setAuthData(null);
    setSelectedCIdState(null);

    // Clear cached billing plan type to prevent stale data across sessions
    clearAccountBillingPlanType();

    // Clear all queries
    queryClient.clear();
  }, [queryClient]);

  const setSelectedCId = useCallback((cId: number) => {
    setSelectedCIdState(cId);
    sessionStorage.setItem(STORAGE_KEYS.SELECTED_CID, String(cId));

    // Invalidate feature queries that depend on selectedCId
    queryClient.invalidateQueries({
      predicate: (query) => {
        // Invalidate queries that have 'byCustomer' in their key
        const key = query.queryKey;
        return Array.isArray(key) && key.some(k => k === 'byCustomer');
      }
    });
  }, [queryClient]);

  const value: AuthContextValue = useMemo(() => ({
    authData,
    isAuthenticated,
    isLoading: isLoading || isCustomerLoading,
    customerInfo: customerInfo ?? null,
    selectedCId,
    selectedCustomer,
    subcustomers,
    canAccessSubAccounts,
    isSubaccount,
    isParentAccount,
    isUploadOnly,
    canViewMedia,
    login,
    logout,
    setSelectedCId,
  }), [
    authData,
    isAuthenticated,
    isLoading,
    isCustomerLoading,
    customerInfo,
    selectedCId,
    selectedCustomer,
    subcustomers,
    canAccessSubAccounts,
    isSubaccount,
    isParentAccount,
    isUploadOnly,
    canViewMedia,
    login,
    logout,
    setSelectedCId,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
